import React, { useEffect, useState } from 'react';
import { deliveryAreaApi, DeliveryArea, DeliveryAreaSearchRebuildResult, DistrictSummary, Province } from '../services/deliveryAreaApi';
import { 
  CheckCircle, XCircle, MapPin, Upload, ChevronDown, ChevronRight, Plus, List, Building, RefreshCw, Search
} from 'lucide-react';
import NeighborhoodsManagementTab from '../components/NeighborhoodsManagementTab';
import CustomLocationsManagementTab from '../components/CustomLocationsManagementTab';

export default function DeliveryAreas() {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [selectedProvince, setSelectedProvince] = useState<Province | null>(null);
  const [districts, setDistricts] = useState<DistrictSummary[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [areas, setAreas] = useState<DeliveryArea[]>([]);
  const [customLocations, setCustomLocations] = useState<Record<string, any[]>>({});
  const [expandedCustomLocations, setExpandedCustomLocations] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [expandedDistricts, setExpandedDistricts] = useState<Set<string>>(new Set());
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [rebuildingSearchIndex, setRebuildingSearchIndex] = useState(false);
  const [searchRebuildResult, setSearchRebuildResult] = useState<DeliveryAreaSearchRebuildResult | null>(null);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | null>(null);
  const [newLocation, setNewLocation] = useState({
    locationName: '',
    locationType: 'building' as 'building' | 'street' | 'complex' | 'landmark',
    deliveryFee: 30,
    neighborhoodName: '',
  });
  const [activeTab, setActiveTab] = useState<'classic' | 'neighborhoods' | 'custom-locations'>('neighborhoods');


  useEffect(() => {
    loadProvinces();
  }, []);

  useEffect(() => {
    if (selectedProvince) {
      loadDistrictsSummary();
    } else {
      setDistricts([]);
    }
  }, [selectedProvince]);

  const loadProvinces = async () => {
    try {
      setLoading(true);
      const data = await deliveryAreaApi.getProvincesList();
      setProvinces(data.provinces);
    } catch (error) {
      console.error('Error loading provinces:', error);
      alert('İl listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadDistrictsSummary = async () => {
    if (!selectedProvince) return;
    
    try {
      setLoading(true);
      const data = await deliveryAreaApi.getDistrictsSummary(selectedProvince.name);
      setDistricts(data.districts);
    } catch (error) {
      console.error('Error loading districts:', error);
      alert('İlçe özeti yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Helper: Normalize Turkish characters for API queries
  const normalizeDistrict = (text: string): string => {
    return text
      .normalize('NFC')
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/İ/gi, 'i')  // Capital İ and lowercase i
      .replace(/\u0307/g, '')  // Remove combining dot above
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]/g, '');
  };

  const loadNeighborhoods = async (district: string) => {
    if (!selectedProvince) return;
    
    try {
      const normalizedDistrict = normalizeDistrict(district);
      console.log('🔍 Loading neighborhoods:', { 
        original: district, 
        normalized: normalizedDistrict,
        province: selectedProvince.name 
      });
      
      const data = await deliveryAreaApi.getAreas({ 
        province: selectedProvince.name,
        district: normalizedDistrict
      });
      setAreas(data.data);
      
      // Auto-load custom locations for all neighborhoods
      const locationPromises = data.data.map(async (area: DeliveryArea) => {
        try {
          const { locations } = await deliveryAreaApi.getCustomLocations(area.id);
          return { neighborhoodId: area.id, locations };
        } catch (error) {
          console.error(`Error loading custom locations for ${area.id}:`, error);
          return { neighborhoodId: area.id, locations: [] };
        }
      });
      
      const locationsResults = await Promise.all(locationPromises);
      const locationsMap = locationsResults.reduce((acc, { neighborhoodId, locations }) => {
        acc[neighborhoodId] = locations;
        return acc;
      }, {} as Record<string, any[]>);
      
      setCustomLocations(locationsMap);
    } catch (error) {
      console.error('Error loading neighborhoods:', error);
      alert('Mahalleler yüklenemedi');
    }
  };

  const handleImport = async () => {
    if (!selectedProvince) {
      alert('Lütfen önce bir il seçin');
      return;
    }

    if (!confirm(`${selectedProvince.name} verilerini import etmek istediğinize emin misiniz?\n\nBu işlem arka planda çalışacak ve birkaç dakika sürebilir.`)) return;

    try {
      setImporting(true);
      const result = await deliveryAreaApi.importProvinceData(selectedProvince.id, selectedProvince.name);
      
      alert(`✅ Import başlatıldı!\n\n${selectedProvince.name} verileri arka planda import ediliyor.\n\nBirkaç dakika sonra sayfayı yenileyerek sonuçları görebilirsiniz.`);
      
      // Auto refresh after 2 minutes
      setTimeout(() => {
        loadDistrictsSummary();
      }, 120000);
      
    } catch (error: any) {
      alert(`❌ Import hatası:\n\n${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleRebuildSearchIndex = async () => {
    const scopeLabel = selectedProvince?.name || 'tüm iller';
    if (!confirm(`${scopeLabel} için arama indeksini yeniden oluşturmak istiyor musunuz?`)) {
      return;
    }

    try {
      setRebuildingSearchIndex(true);
      const result = await deliveryAreaApi.rebuildSearchIndex(selectedProvince?.name);
      setSearchRebuildResult(result);
      alert(`✅ Arama indeksi güncellendi.\n\nİşlenen kayıt: ${result.processed}\nToplam search term: ${result.totalSearchTerms}`);
      if (selectedProvince) {
        loadDistrictsSummary();
      }
    } catch (error: any) {
      alert(`❌ Hata:\n\n${error.message}`);
    } finally {
      setRebuildingSearchIndex(false);
    }
  };

  const toggleDistrict = (districtName: string) => {
    const newExpanded = new Set(expandedDistricts);
    if (newExpanded.has(districtName)) {
      newExpanded.delete(districtName);
    } else {
      newExpanded.add(districtName);
      loadNeighborhoods(districtName);
    }
    setExpandedDistricts(newExpanded);
    setSelectedDistrict(districtName);
  };

  const toggleAreaStatus = async (areaId: string, currentStatus: boolean) => {
    try {
      await deliveryAreaApi.updateArea(areaId, { isActive: !currentStatus });
      
      // Reload neighborhoods
      if (selectedDistrict) {
        loadNeighborhoods(selectedDistrict);
      }
      loadDistrictsSummary();
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    }
  };

  const bulkUpdateDistrict = async (district: string, updates: any) => {
    if (!selectedProvince) {
      alert('Lütfen önce bir il seçin');
      return;
    }

    if (!confirm(`${district} ilçesindeki tüm mahalleleri güncellemek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await deliveryAreaApi.bulkUpdate(selectedProvince.name, district.toLowerCase(), updates);
      alert('✅ Güncelleme başarılı!');
      loadNeighborhoods(district);
      loadDistrictsSummary();
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    }
  };

  const updateDeliveryFee = (district: string) => {
    const fee = prompt(`${district} için teslimat ücreti (TL):`, '30');
    if (fee && !isNaN(Number(fee))) {
      bulkUpdateDistrict(district, { deliveryFee: Number(fee) });
    }
  };

  // Custom Locations Management
  const loadCustomLocations = async (neighborhoodId: string) => {
    try {
      const { locations } = await deliveryAreaApi.getCustomLocations(neighborhoodId);
      setCustomLocations(prev => ({ ...prev, [neighborhoodId]: locations }));
    } catch (error) {
      console.error('Error loading custom locations:', error);
    }
  };

  const openAddLocationModal = (neighborhoodId: string, neighborhoodName: string) => {
    setSelectedNeighborhoodId(neighborhoodId);
    setNewLocation({
      locationName: '',
      locationType: 'building',
      deliveryFee: 30,
      neighborhoodName
    });
    setShowAddLocationModal(true);
  };

  const handleAddLocation = async () => {
    if (!selectedNeighborhoodId || !newLocation.locationName.trim()) {
      alert('Lütfen konum adını girin');
      return;
    }

    try {
      await deliveryAreaApi.addCustomLocation({
        neighborhoodId: selectedNeighborhoodId,
        locationName: newLocation.locationName.trim(),
        locationType: newLocation.locationType,
        deliveryFee: newLocation.deliveryFee || 30
      });
      
      alert('✅ Konum başarıyla eklendi!');
      await loadCustomLocations(selectedNeighborhoodId);
      setShowAddLocationModal(false);
      setNewLocation({
        locationName: '',
        locationType: 'building',
        deliveryFee: 30,
        neighborhoodName: ''
      });
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    }
  };

  const handleDeleteLocation = async (locationId: string, neighborhoodId: string) => {
    if (!confirm('Bu konumu silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await deliveryAreaApi.deleteCustomLocation(locationId);
      alert('✅ Konum silindi!');
      await loadCustomLocations(neighborhoodId);
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Teslimat Alanı Yönetimi</h1>
        
        {/* Province Selection & Import */}
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              İl Seçin
            </label>
            <select
              value={selectedProvince?.id || ''}
              onChange={(e) => {
                const province = provinces.find(p => p.id === Number(e.target.value));
                setSelectedProvince(province || null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              <option value="">-- İl Seçin --</option>
              {provinces.map((province) => (
                <option key={province.id} value={province.id}>
                  {province.name} ({province.districts.length} ilçe)
                </option>
              ))}
            </select>
          </div>
          
          <button
            onClick={handleImport}
            disabled={importing || !selectedProvince}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Upload size={20} />
            {importing ? 'Import Ediliyor...' : 'İl Verilerini Import Et'}
          </button>

          <button
            onClick={handleRebuildSearchIndex}
            disabled={rebuildingSearchIndex}
            className="flex items-center gap-2 px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {rebuildingSearchIndex ? <RefreshCw size={20} className="animate-spin" /> : <Search size={20} />}
            {rebuildingSearchIndex ? 'İndeks Yenileniyor...' : 'Arama İndeksini Yeniden Oluştur'}
          </button>
        </div>

        {searchRebuildResult && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-amber-900">Son Arama İndeksi Sonucu</h2>
                <p className="mt-1 text-sm text-amber-800">
                  Kapsam: {searchRebuildResult.province || 'Tüm iller'}
                </p>
              </div>
              <div className="text-right text-sm text-amber-900">
                <p><strong>İşlenen:</strong> {searchRebuildResult.processed}</p>
                <p><strong>Güncellenen:</strong> {searchRebuildResult.updated}</p>
                <p><strong>Toplam search term:</strong> {searchRebuildResult.totalSearchTerms}</p>
                <p><strong>Ortalama:</strong> {searchRebuildResult.averageSearchTermsPerDoc}</p>
              </div>
            </div>

            {searchRebuildResult.samples.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-lg border border-amber-200 bg-white">
                <table className="min-w-full divide-y divide-amber-100 text-sm">
                  <thead className="bg-amber-100/60">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-amber-900">Kayıt</th>
                      <th className="px-4 py-2 text-left font-semibold text-amber-900">Alias Önizleme</th>
                      <th className="px-4 py-2 text-left font-semibold text-amber-900">Term</th>
                      <th className="px-4 py-2 text-left font-semibold text-amber-900">Özel Konum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {searchRebuildResult.samples.map((sample) => (
                      <tr key={sample.id}>
                        <td className="px-4 py-2 text-gray-800">{sample.fullName}</td>
                        <td className="px-4 py-2 text-gray-700">{sample.aliasPreview.join(', ') || '-'}</td>
                        <td className="px-4 py-2 text-gray-700">{sample.termCount}</td>
                        <td className="px-4 py-2 text-gray-700">{sample.customLocationCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      {activeTab === 'classic' && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle size={24} />
              <span className="text-2xl font-bold">
                {districts.reduce((sum, d) => sum + d.active, 0)}
              </span>
            </div>
            <p className="text-green-600 mt-1">Aktif Mahalle</p>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-center gap-2 text-red-700">
              <XCircle size={24} />
              <span className="text-2xl font-bold">
                {districts.reduce((sum, d) => sum + d.inactive, 0)}
              </span>
            </div>
            <p className="text-red-600 mt-1">Pasif Mahalle</p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 text-blue-700">
              <MapPin size={24} />
              <span className="text-2xl font-bold">{districts.length}</span>
            </div>
            <p className="text-blue-600 mt-1">İlçe</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('classic')}
              className={`${
                activeTab === 'classic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <List size={18} />
              Klasik Görünüm
            </button>
            <button
              onClick={() => setActiveTab('neighborhoods')}
              className={`${
                activeTab === 'neighborhoods'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <MapPin size={18} />
              Mahalleler Yönetimi
            </button>
            <button
              onClick={() => setActiveTab('custom-locations')}
              className={`${
                activeTab === 'custom-locations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <Building size={18} />
              Özel Konumlar Yönetimi
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'classic' && (
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
          ) : districts.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 mb-4">Henüz veri yok. Import butonuna tıklayarak başlayın.</p>
            </div>
          ) : (
            <div className="divide-y">
              {districts.map((district) => (
                <div key={district.name}>
                  {/* District Header */}
                  <div
                    className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => toggleDistrict(district.name)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedDistricts.has(district.name) ? (
                        <ChevronDown size={20} className="text-gray-600" />
                      ) : (
                        <ChevronRight size={20} className="text-gray-600" />
                      )}
                      <MapPin size={20} className="text-blue-600" />
                      <span className="font-semibold text-lg text-gray-800">{district.name}</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">
                        {district.active} / {district.total} aktif
                      </span>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateDeliveryFee(district.name);
                        }}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                      >
                        Toplu Ücret Güncelle
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          bulkUpdateDistrict(district.name, { isActive: true });
                        }}
                        className="px-3 py-1 text-sm bg-green-100 text-green-700 hover:bg-green-200 rounded transition-colors"
                      >
                        Tümünü Aktif Et
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          bulkUpdateDistrict(district.name, { isActive: false });
                        }}
                        className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded transition-colors"
                      >
                        Tümünü Pasif Et
                      </button>
                    </div>
                  </div>

                  {/* Neighborhoods */}
                  {expandedDistricts.has(district.name) && (
                    <div className="bg-gray-50 px-4 py-2">
                      {areas
                        .filter((a) => a.district === district.name)
                        .map((area) => (
                          <div key={area.id} className="mb-4">
                            {/* Neighborhood Header */}
                            <div className="flex items-center justify-between py-2 px-4 bg-white mb-1 rounded border hover:border-gray-300 transition-colors">
                              <div className="flex items-center gap-3">
                                <span className="text-gray-700 font-medium">{area.neighborhood}</span>
                                {area.notes && (
                                  <span className="text-xs text-gray-500 italic">({area.notes})</span>
                                )}
                              </div>

                              <div className="flex items-center gap-4">
                                <span className="text-sm font-semibold text-gray-800">{area.deliveryFee}₺</span>
                                <span className="text-xs text-gray-500">{area.estimatedDuration}</span>
                                
                                <button
                                  onClick={() => toggleAreaStatus(area.id, area.isActive)}
                                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                    area.isActive
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                                  }`}
                                >
                                  {area.isActive ? '✓ Aktif' : '✗ Pasif'}
                                </button>

                                <button
                                  onClick={() => openAddLocationModal(area.id, `${selectedProvince?.name} - ${district.name} - ${area.neighborhood}`)}
                                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors flex items-center gap-1"
                                >
                                  <Plus size={16} />
                                  Bina/Sokak Ekle
                                </button>
                              </div>
                            </div>

                            {/* Custom Locations - Toggle visibility */}
                            <div className="ml-8 mt-1">
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedCustomLocations);
                                  if (newExpanded.has(area.id)) {
                                    newExpanded.delete(area.id);
                                  } else {
                                    newExpanded.add(area.id);
                                  }
                                  setExpandedCustomLocations(newExpanded);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-1"
                              >
                                {expandedCustomLocations.has(area.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                📍 Özel Konumlar ({customLocations[area.id]?.length || 0})
                              </button>
                              
                              {expandedCustomLocations.has(area.id) && customLocations[area.id]?.map((location) => (
                                  <div
                                    key={location.id}
                                    className="flex items-center justify-between py-1 px-3 bg-blue-50 mb-1 rounded border border-blue-200 text-sm"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>
                                        {location.locationType === 'building' && '🏢'}
                                        {location.locationType === 'street' && '🛣️'}
                                        {location.locationType === 'complex' && '🏘️'}
                                        {location.locationType === 'landmark' && '📍'}
                                      </span>
                                      <span className="text-gray-700">{location.locationName}</span>
                                      <span className="text-xs text-gray-500">({location.locationType})</span>
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <span className="text-xs font-semibold text-gray-800">{location.deliveryFee}₺</span>
                                      <button
                                        onClick={() => handleDeleteLocation(location.id, area.id)}
                                        className="text-red-600 hover:text-red-800 text-xs"
                                      >
                                        Sil
                                      </button>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'neighborhoods' && (
        <NeighborhoodsManagementTab 
          provinces={provinces} 
          selectedProvince={selectedProvince} 
        />
      )}

      {activeTab === 'custom-locations' && (
        <CustomLocationsManagementTab 
          provinces={provinces} 
          selectedProvince={selectedProvince} 
        />
      )}

      {/* Add Custom Location Modal */}
      {showAddLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Özel Konum Ekle</h2>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Mahalle:</strong> {newLocation.neighborhoodName}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Konum Adı *
              </label>
              <input
                type="text"
                value={newLocation.locationName}
                onChange={(e) => setNewLocation({...newLocation, locationName: e.target.value})}
                placeholder="Örn: Yaprak Evleri 1, Atatürk Caddesi, Bahçelievler Sitesi"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Konum Tipi *
              </label>
              <select
                value={newLocation.locationType}
                onChange={(e) => setNewLocation({...newLocation, locationType: e.target.value as 'building' | 'street' | 'complex' | 'landmark'})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="building">🏢 Bina</option>
                <option value="street">🛣️ Sokak/Cadde</option>
                <option value="complex">🏘️ Site/Kompleks</option>
                <option value="landmark">📍 İşaret Noktası</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teslimat Ücreti (TL)
              </label>
              <input
                type="number"
                value={newLocation.deliveryFee}
                onChange={(e) => setNewLocation({...newLocation, deliveryFee: Number(e.target.value)})}
                min="0"
                step="5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAddLocation}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Ekle
              </button>
              <button
                onClick={() => setShowAddLocationModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
